import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { toast } from 'sonner';
import dayjs from 'dayjs';
import type { DateRange } from 'react-day-picker';
import { RotateCw } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DateRangePicker } from '@/components/shared/DateRangePicker';
import { CopyValueButton } from '@/components/shared/CopyValueButton';
import { isRowNavHandled } from '@/components/shared/rowNav';
import { FiscalInvoiceStatusBadge } from '@/components/fiscal/FiscalStatusBadge';
import { FiscalAccessDenied } from '@/components/fiscal/FiscalAccessDenied';
import { shortenMiddle } from '@/components/fiscal/fiscalFormat';
import { FiscalInvoiceDetailDialog } from './FiscalInvoiceDetailDialog';
import type { AppShellContext } from '@/components/layout/AppShell';
import { useFiscalApi, type FiscalInvoiceListItem } from '@/services/api';
import { computeFiscalInvoiceGross } from '@/utils/fiscalAmounts';
import { formatPLN } from '@/utils/currency';
import { t } from '@/i18n';
import { FISCAL_INVOICE_STATUSES, type FiscalInvoiceStatus } from '@ip/shared-types';
import { Badge } from '@/components/ui/badge';
import { ENV_BADGE_CLASS } from './fiscalEnv';

const POLL_MS = 30_000;
const LIMIT_OPTIONS = [25, 50, 100, 200] as const;

/** KSeF fiscal invoice list — read-only passthrough table with status/search/date filters,
 *  30 s auto-refresh, and a row-click detail dialog (XML/UPO/QR + retry). */
export function FiscalInvoices() {
  const { nip } = useOutletContext<AppShellContext>();
  const { listInvoices } = useFiscalApi();
  const [invoices, setInvoices] = useState<FiscalInvoiceListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<FiscalInvoiceStatus | 'all'>('all');
  const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);
  const [limit, setLimit] = useState<number>(50);
  const [selectedUuid, setSelectedUuid] = useState<string | null>(null);
  const loadSeq = useRef(0);

  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    try {
      const result = await listInvoices(nip, {
        status: status === 'all' ? undefined : status,
        search: search || undefined,
        from: dateRange?.from ? dayjs(dateRange.from).format('YYYY-MM-DD') : undefined,
        to: dateRange?.to ? dayjs(dateRange.to).format('YYYY-MM-DD') : undefined,
        limit,
      });
      if (seq !== loadSeq.current) return;
      setInvoices(result);
      setForbidden(false);
    } catch (error) {
      if (seq !== loadSeq.current) return;
      if ((error as { status?: number })?.status === 403) {
        setForbidden(true);
      } else {
        toast.error(t('errors.loadingFailed'));
      }
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [nip, status, search, dateRange, limit, listInvoices]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  useEffect(() => {
    const timer = setInterval(load, POLL_MS);
    return () => clearInterval(timer);
  }, [load]);

  const rows = useMemo(
    () =>
      invoices.map((inv) => {
        let gross: number | null = null;
        try {
          gross = computeFiscalInvoiceGross(inv.invoice);
        } catch {
          gross = null;
        }
        return { inv, gross };
      }),
    [invoices],
  );

  if (forbidden) return <FiscalAccessDenied />;

  return (
    <div className="flex flex-col gap-3">
      <Card className="flex flex-wrap items-end gap-3 p-3">
        <div className="min-w-[220px] flex-1">
          <p className="text-muted-foreground mb-1 text-xs">{t('common.search')}</p>
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('fiscal.invoices.searchPlaceholder')}
          />
        </div>
        <div>
          <p className="text-muted-foreground mb-1 text-xs">{t('fiscal.columns.status')}</p>
          <Select value={status} onValueChange={(v) => setStatus(v as FiscalInvoiceStatus | 'all')}>
            <SelectTrigger className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('fiscal.invoices.statusAll')}</SelectItem>
              {FISCAL_INVOICE_STATUSES.map((s) => (
                <SelectItem key={s} value={s}>
                  {t(`fiscal.invoiceStatus.${s}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <p className="text-muted-foreground mb-1 text-xs">{t('common.filter')}</p>
          <DateRangePicker value={dateRange} onChange={setDateRange} />
        </div>
        <div>
          <p className="text-muted-foreground mb-1 text-xs">{t('fiscal.invoices.limitLabel')}</p>
          <Select value={String(limit)} onValueChange={(v) => setLimit(Number(v))}>
            <SelectTrigger className="w-[90px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {LIMIT_OPTIONS.map((n) => (
                <SelectItem key={n} value={String(n)}>
                  {n}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button variant="outline" size="sm" onClick={load} aria-label={t('fiscal.invoices.refresh')}>
          <RotateCw className="size-4" />
        </Button>
      </Card>

      <div className="bg-card overflow-x-auto rounded-xl border">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>{t('fiscal.columns.status')}</TableHead>
              <TableHead>{t('fiscal.columns.number')}</TableHead>
              <TableHead>{t('fiscal.columns.buyer')}</TableHead>
              <TableHead className="text-right">{t('fiscal.columns.amount')}</TableHead>
              <TableHead>{t('fiscal.columns.ksefNumber')}</TableHead>
              <TableHead>{t('fiscal.columns.createdAt')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: 8 }, (_, i) => (
                <TableRow key={i}>
                  <TableCell colSpan={6}>
                    <Skeleton className="h-8 w-full" />
                  </TableCell>
                </TableRow>
              ))
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="h-40 text-center">
                  <p className="font-medium">{t('fiscal.invoices.empty.title')}</p>
                  <p className="text-muted-foreground text-sm">{t('fiscal.invoices.empty.description')}</p>
                </TableCell>
              </TableRow>
            ) : (
              rows.map(({ inv, gross }) => (
                <TableRow
                  key={inv.uuid}
                  className="group cursor-pointer"
                  onClick={(e) => {
                    if (isRowNavHandled(e)) return;
                    setSelectedUuid(inv.uuid);
                  }}
                >
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      <FiscalInvoiceStatusBadge status={inv.status} />
                      {/* Środowisko zamrożone na fakturze; brak badge = wiersz
                          sprzed per-token env (obowiązywało ustawienie firmy). */}
                      {inv.environment && (
                        <Badge className={`text-[10px] font-medium uppercase ${ENV_BADGE_CLASS[inv.environment]}`}>
                          {t(`fiscal.environment.${inv.environment}`)}
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="font-medium">{inv.number}</TableCell>
                  <TableCell className="max-w-[220px] truncate" title={inv.invoice.buyer.name}>
                    {inv.invoice.buyer.name}
                  </TableCell>
                  <TableCell className="text-right font-mono tabular-nums">
                    {gross === null ? (
                      <span className="text-muted-foreground">—</span>
                    ) : (
                      `${formatPLN(gross)} ${inv.invoice.currency ?? '—'}`
                    )}
                  </TableCell>
                  <TableCell>
                    {inv.ksefNumber ? (
                      <span className="inline-flex items-center gap-1 font-mono text-xs">
                        {shortenMiddle(inv.ksefNumber)}
                        <CopyValueButton value={inv.ksefNumber} />
                      </span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm whitespace-nowrap">
                    {new Date(inv.createdAt).toLocaleString('pl-PL')}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <FiscalInvoiceDetailDialog
        nip={nip}
        uuid={selectedUuid}
        onOpenChange={(open) => !open && setSelectedUuid(null)}
        onChanged={load}
      />
    </div>
  );
}
